import { inject, Injectable } from "@angular/core";
import { ApicallOption, ApicallService } from "@core-services/apicall.service";
import { BehaviorSubject, EMPTY, Observable } from "rxjs";
import { catchError, map, tap } from "rxjs/operators";
import { Helper, parseApiResponseObject2 } from "@helpers/functions";
import {
	DeclaredTaxesModel,
	PriceVerificationModel,
	SelfCertificationModel,
	ITaxWithheld,
	TaxesMultiDeclarationModel,
	TaxesPriceVerificationModel,
	TaxesUndeclaredTrxModel,
	TaxesUserStatusModel,
	ITaxInOffer,
	CapitalGainsTaxModel
} from "@modules/taxes/domain/entities/taxes.model";
import { config } from "@config";
import { BciTradeAction, CreateBciTaxGQLService, CreateBciTaxRequest, CreateBciTaxResponse } from "@gql/offer";
import { ExceptionsService } from "@core-services/exceptions.service";
import { TAXES_NOT_ELIGIBLE_COOKIE_KEY } from "@helpers/constants";
import { FiatTransaction } from "@models/transaction";
import { TransactionInfo } from "../entities/transaction-info.model";
import { TaxesUserState } from "@modules/taxes/domain/+state/taxes.state.service";

export interface TaxesMultiDeclarationApiModel {
	transactions: TaxesUndeclaredTransactionsApiModel[];
}

interface TaxesUndeclaredTransactionsApiModel {
	transaction_id: string;
	asset_amount: number;
	asset_symbol: string;
	asset_name: string;
	wallet_id: string;
	asset_id: string;
	credited_at: string;
}

interface UserTaxStatusApiModel {
	tax_eligibility_status?: SelfCertificationModel;
	open_declarations_count?: number;
}

interface PriceVerificationApiModel {
	higher_than_first_boundary: boolean;
	higher_than_second_boundary: boolean;
}

export interface IBCITaxCreateRequest {
	amount: string;
	assetId: string;
	fiatId: string;
	type: BciTradeAction;
}

interface PostUndeclaredTrxApiModel {
	fiat_id: string;
	declarations: TaxesUndeclaredTrxModel[];
}

interface CapitalGainsTaxApiModel {
	capital_gain: string;
	taxable_amount: string;
}

export enum TinVerificationState {
	Pending = "pending",
	InReview = "in_review",
	OnHold = "on_hold"
}
export interface ITinVerificationWidgetStatusResponse {
	trading_state: TinVerificationState;
	title: string;
	small_title: string;
	text: string;
	cta_title: string;
	flagOn: boolean;
	hasError: boolean;
}

export interface ITinVerificationStatusResponse {
	status: TinVerificationState;
	flagOn: boolean;
	hasError: boolean;
}

@Injectable({
	providedIn: "root"
})
export class TaxesRemote {
	private readonly apicallService = inject(ApicallService);
	private readonly requestBCItaxCreateService = inject(CreateBciTaxGQLService);
	private readonly exceptionsService = inject(ExceptionsService);
	private taxStatusSource = new BehaviorSubject<TaxesUserState>({
		taxStatus: null,
		hasError: null,
		isLoading: false,
		declarationsOpen: null,
		isUpdatingStatus: false,
		declarationsOpenForWallet: null
	});

	public getUserTaxStatus$(walletId?: string): Observable<TaxesUserStatusModel> {
		const option = new ApicallOption();

		if (walletId) {
			option.params = { wallet_id: walletId };
		}

		return this.apicallService.get("tax-withholding/users/tax-status", option).pipe(
			map((response) => parseApiResponseObject2<UserTaxStatusApiModel>(response)),
			map((response) => ({
				taxStatus: response.tax_eligibility_status,
				openDeclarationsCount: response.open_declarations_count
			})),
			tap((data) =>
				this.taxStatusSource.next({
					taxStatus: null,
					hasError: null,
					isLoading: false,
					declarationsOpen: null,
					isUpdatingStatus: false,
					declarationsOpenForWallet: null
				})
			),
			catchError((error) => {
				this.taxStatusSource.next({
					taxStatus: null,
					hasError: error,
					isLoading: false,
					declarationsOpen: null,
					isUpdatingStatus: false,
					declarationsOpenForWallet: null
				});
				throw error;
			})
		);
	}

	public updateUserTaxStatus$(userStatus: boolean, walletId?: string): Observable<TaxesUserStatusModel> {
		const option = new ApicallOption(null, userStatus);
		if (walletId) {
			option.params = { wallet_id: walletId };
		}
		return this.apicallService.post("tax-withholding/users/declare", option).pipe(
			map((response) => parseApiResponseObject2<UserTaxStatusApiModel>(response)),
			map((response) => {
				// Set cookie if not already set
				if (!userStatus && Helper.getCookie(TAXES_NOT_ELIGIBLE_COOKIE_KEY, true) !== "true") {
					Helper.setCookie(TAXES_NOT_ELIGIBLE_COOKIE_KEY, "true", true);
				}
				return {
					taxStatus: response.tax_eligibility_status
				};
			})
		);
	}

	public getUndeclaredTransactions$(walletId?: string): Observable<TaxesMultiDeclarationModel> {
		const option = new ApicallOption();
		if (walletId) {
			option.params = { wallet_id: walletId };
		}
		return this.apicallService.get("tax-withholding/transactions/types/undeclared/", option).pipe(
			map((response) => parseApiResponseObject2<TaxesMultiDeclarationApiModel>(response)),
			map((response) => {
				const transactions = response.transactions.map((transaction) => {
					return {
						transactionId: transaction.transaction_id,
						assetAmount: transaction.asset_amount,
						assetSymbol: transaction.asset_symbol,
						assetName: transaction.asset_name,
						walletId: transaction.wallet_id,
						assetId: transaction.asset_id,
						creditedAt: transaction.credited_at,
						assetLogo: `${config.staticAssetLink}cryptocoin/${transaction.asset_id.toLowerCase()}.svg`
					};
				});
				return { transactions };
			})
		);
	}

	public updateUndeclaredTransactions$(
		fiatPid: string,
		undeclaredTrx: TaxesUndeclaredTrxModel[]
	): Observable<TaxesUserStatusModel> {
		const postUndeclaredTrx: PostUndeclaredTrxApiModel = {
			fiat_id: fiatPid,
			declarations: undeclaredTrx
		};
		const option = new ApicallOption(null, postUndeclaredTrx);
		return this.apicallService.post("tax-withholding/transactions/declare", option).pipe(
			map((response) => parseApiResponseObject2<UserTaxStatusApiModel>(response)),
			map((response) => {
				return {
					openDeclarationsCount: response.open_declarations_count
				};
			})
		);
	}

	public getTaxesInfo$(transactionId: string): Observable<DeclaredTaxesModel> {
		return this.apicallService.get(`tax-withholding/transactions/${transactionId}/details`).pipe(
			map((response) => parseApiResponseObject2<DeclaredTaxesModel>(response)),
			map((response) => {
				return {
					fiat_id: response.fiat_id,
					status: response.status,
					declarations: response.declarations
				};
			})
		);
	}

	public getCapitalGainsTax$(transactionId: string, isFiatTrx: boolean): Observable<CapitalGainsTaxModel> {
		const option = new ApicallOption();
		isFiatTrx
			? (option.params = { fiat_transaction_id: transactionId })
			: (option.params = { transaction_id: transactionId });

		return this.apicallService.get(`tax-withholding/transactions/tax`, option).pipe(
			map((response) => parseApiResponseObject2<CapitalGainsTaxApiModel>(response)),
			map((response) => {
				return {
					capitalGain: response.capital_gain,
					taxableAmount: response.taxable_amount
				};
			})
		);
	}

	public getPriceVerification$(priceVerification: TaxesPriceVerificationModel): Observable<PriceVerificationModel> {
		const option = new ApicallOption(null, priceVerification);
		return this.apicallService.post("tax-withholding/prices/verify", option).pipe(
			map((response) => parseApiResponseObject2<PriceVerificationApiModel>(response)),
			map((response) => {
				return {
					higherThanFirstBoundary: response.higher_than_first_boundary,
					higherThanSecondBoundary: response.higher_than_second_boundary
				};
			})
		);
	}

	public createBCITaxGQL(request: IBCITaxCreateRequest): Observable<ITaxInOffer> {
		return this.requestBCItaxCreateService.mutate({ input: serializeBCITaxCreateRequest(request) }).pipe(
			map(({ data }) => deserializeBCITaxCreateResponse(data.createBciTax)),
			catchError(this.exceptionsService.extractGqlErrorAndRethrow)
		);
	}

	public getTaxRefundRelatedTX$(fiatWalletId: string, transactionId: string): Observable<FiatTransaction> {
		return this.getTransactionDetailRemote(fiatWalletId, transactionId).pipe(catchError((error) => EMPTY));
	}

	public getTransactionDetailRemote(fiatWalletId: string, transactionId: string): Observable<FiatTransaction> {
		return this.apicallService
			.get("fiatwallet/" + fiatWalletId + "/transaction/" + transactionId, new ApicallOption(FiatTransaction))
			.pipe(
				map((res: FiatTransaction[]) => {
					if (!res || !res.length) {
						throw new Error("No transaction found");
					}
					return res[0];
				})
			);
	}

	public getTransactionDetailBCIForSwapRemote(
		fiatWalletId: string,
		transactionId: string
	): Observable<FiatTransaction> {
		const option = new ApicallOption(FiatTransaction);
		option.params = {
			page_size: 25
		};

		return this.apicallService
			.get("wallet/" + fiatWalletId + "/transactions/" + transactionId + "/trades", option)
			.pipe(
				map((res) => {
					if (!res || !res.length) {
						throw new Error("No transaction found");
					}

					return res;
				})
			);
	}

	public getTransactionInfo(id: string): Observable<TransactionInfo> {
		const option = new ApicallOption();
		return this.apicallService.get("payments/transactions/" + id, option);
	}

	getTinVerificationWidgetStatus(): Observable<Partial<ITinVerificationWidgetStatusResponse>> {
		const option = new ApicallOption();
		return this.apicallService.get("additional-verification/tin/user/widget", option);
	}

	getTinVerificationStatus(): Observable<Partial<ITinVerificationStatusResponse>> {
		const option = new ApicallOption();
		return this.apicallService.get("additional-verification/tin/user/status", option);
	}
}

/**
 * serialize bci tax create request to API DTO
 *
 * @param request - request received from argument
 * @returns - updated request DTO - for GQL API
 */

export const serializeBCITaxCreateRequest = (request: IBCITaxCreateRequest): CreateBciTaxRequest => {
	const { amount, assetId, fiatId, type } = request;
	return {
		amount: { value: amount },
		assetId,
		fiatId,
		type
	};
};

/**
 * deserialize bci tax create response from API DTO
 *
 * @param response - request received from API DTO
 * @returns - ITaxWithheld
 */

export const deserializeBCITaxCreateResponse = (response: CreateBciTaxResponse): ITaxInOffer => {
	let _taxWithheld: Partial<ITaxWithheld> = null;

	const isTaxable = response?.isTaxable;

	if (response?.taxWithheld) {
		const { assetId, fiatId, fiatAmountAfterTax, taxAmountAsset, taxAmountFiat, isTaxServiceAvailable } =
			response.taxWithheld;

		_taxWithheld = {
			isTaxServiceAvailable,
			assetId,
			fiatId,
			fiatAmountAfterTax: fiatAmountAfterTax?.value,
			taxAmountAsset: taxAmountAsset?.value,
			taxAmountFiat: taxAmountFiat?.value
		};
	}

	return {
		isTaxable,
		tax: _taxWithheld
	};
};
